// Wellness scoring engine — rule-based, deterministic
// No AI, no external calls

export interface WellnessInput {
  sleepQuality: number;       // 1-5
  sleepDuration: number;      // hours
  sorenessLevel: number;      // 1-5
  stressLevel: number;        // 1-5
  energyLevel: number;        // 1-5
  mood: number;               // 1-5
  nutritionQuality: number;   // 1-5
  hydrationQuality: number;   // 1-5
  bodyPain: boolean;
  painLocation?: string;
  practiceYesterday: boolean;
  practiceToday: boolean;
  notesToCoach?: string;
}

export type ReadinessStatus = 'green' | 'yellow' | 'orange' | 'red';

export interface ReadinessResult {
  score: number;
  status: ReadinessStatus;
  statusLabel: string;
  statusColor: string;
  statusBg: string;
  statusBorder: string;
  trainingAdjustment: string;
  interpretation: string;
  suggestedFocus: string[];
}

export interface WellnessCheckin extends WellnessInput {
  id?: string;
  athleteId: string;
  checkinDate: string;
  readinessScore: number;
  readinessStatus: ReadinessStatus;
  trainingAdjustment: string;
  createdAt?: string;
}

// ─── Score Calculation ────────────────────────────────────────────────────────
// Weights: Sleep 25%, Soreness 20%, Stress 15%, Energy 20%, Mood 10%, Nutrition+Hydration 10%

function normalize15(val: number): number {
  // 1-5 scale → 0-100
  return ((val - 1) / 4) * 100;
}

function sleepDurationScore(hours: number): number {
  // Optimal: 7-9 hrs = 100, <5 or >11 = 0
  if (hours >= 7 && hours <= 9) return 100;
  if (hours >= 6 && hours < 7) return 75;
  if (hours >= 9 && hours <= 10) return 85;
  if (hours >= 5 && hours < 6) return 45;
  if (hours > 10 && hours <= 11) return 60;
  if (hours < 5) return 15;
  return 30;
}

export function calculateReadinessScore(input: WellnessInput): number {
  const sleepQualityNorm = normalize15(input.sleepQuality);
  const sleepDurNorm = sleepDurationScore(input.sleepDuration);
  const sleepScore = (sleepQualityNorm * 0.6 + sleepDurNorm * 0.4);

  // Soreness: inverse (higher soreness = lower score)
  const sorenessScore = normalize15(6 - input.sorenessLevel);

  // Stress: inverse
  const stressScore = normalize15(6 - input.stressLevel);

  const energyScore = normalize15(input.energyLevel);
  const moodScore = normalize15(input.mood);

  const nutritionHydrationScore = (normalize15(input.nutritionQuality) + normalize15(input.hydrationQuality)) / 2;

  let raw =
    sleepScore * 0.25 +
    sorenessScore * 0.20 +
    stressScore * 0.15 +
    energyScore * 0.20 +
    moodScore * 0.10 +
    nutritionHydrationScore * 0.10;

  // Pain penalty: drop to max 49 (forces Red)
  if (input.bodyPain) {
    raw = Math.min(raw, 49);
  }

  return Math.round(Math.max(0, Math.min(100, raw)));
}

// ─── Status Classification ────────────────────────────────────────────────────

export function getReadinessStatus(score: number): ReadinessStatus {
  if (score >= 85) return 'green';
  if (score >= 70) return 'yellow';
  if (score >= 50) return 'orange';
  return 'red';
}

export const STATUS_META: Record<ReadinessStatus, {
  label: string;
  color: string;
  bg: string;
  border: string;
  textClass: string;
  bgClass: string;
  borderClass: string;
  dotClass: string;
}> = {
  green: {
    label: 'Ready to Train',
    color: '#34d399',
    bg: 'rgba(52,211,153,0.08)',
    border: 'rgba(52,211,153,0.25)',
    textClass: 'text-emerald-400',
    bgClass: 'bg-emerald-400/10',
    borderClass: 'border-emerald-400/25',
    dotClass: 'bg-emerald-400',
  },
  yellow: {
    label: 'Monitor Load',
    color: '#fbbf24',
    bg: 'rgba(251,191,36,0.08)',
    border: 'rgba(251,191,36,0.25)',
    textClass: 'text-amber-400',
    bgClass: 'bg-amber-400/10',
    borderClass: 'border-amber-400/25',
    dotClass: 'bg-amber-400',
  },
  orange: {
    label: 'Modify Training',
    color: '#fb923c',
    bg: 'rgba(251,146,60,0.08)',
    border: 'rgba(251,146,60,0.25)',
    textClass: 'text-orange-400',
    bgClass: 'bg-orange-400/10',
    borderClass: 'border-orange-400/25',
    dotClass: 'bg-orange-400',
  },
  red: {
    label: 'Recovery Priority',
    color: '#f87171',
    bg: 'rgba(248,113,113,0.08)',
    border: 'rgba(248,113,113,0.25)',
    textClass: 'text-red-400',
    bgClass: 'bg-red-400/10',
    borderClass: 'border-red-400/25',
    dotClass: 'bg-red-400',
  },
};

// ─── Training Adjustment Rules ────────────────────────────────────────────────

export function getTrainingAdjustment(status: ReadinessStatus, input: WellnessInput): string {
  if (input.bodyPain) {
    return 'Recovery session only. No max-intent work. Prioritize mobility, tissue prep, breathing, and light movement.';
  }
  switch (status) {
    case 'green':
      return 'Complete planned training. Allow high-intent sprint, jump, and lift work.';
    case 'yellow':
      return 'Keep main session. Reduce total volume by 10-20%. Monitor soreness and energy.';
    case 'orange':
      return 'Reduce volume by 25-40%. Remove max-intent sprinting or heavy lifting. Use technique, mobility, tempo, and low-level power work.';
    case 'red':
      return 'Recovery session only. No max-intent work. Prioritize mobility, tissue prep, breathing, and light movement.';
  }
}

export function getInterpretation(score: number, input: WellnessInput): string {
  if (input.bodyPain) {
    return `Pain reported in ${input.painLocation || 'unspecified location'}. Athlete flagged for modified or no high-intensity training. Pain history logged.`;
  }
  if (score >= 85) return 'Athlete is well-recovered and primed for high-output training. All systems go.';
  if (score >= 70) return 'Athlete is functional but showing mild fatigue indicators. Manage load carefully today.';
  if (score >= 50) return 'Multiple wellness markers are suppressed. Training intensity must be reduced to avoid overreach.';
  return 'Athlete is in a compromised recovery state. Full recovery session recommended — no high-intensity work.';
}

export function getSuggestedFocus(status: ReadinessStatus, input: WellnessInput): string[] {
  if (input.bodyPain) {
    return ['Soft tissue work', 'Breathing drills', 'Walking / light movement', 'Pain assessment'];
  }
  switch (status) {
    case 'green':
      return ['Sprint / jump work', 'Heavy compound lifts', 'Power development', 'Full program execution'];
    case 'yellow':
      return ['Main lifts at reduced volume', 'Moderate-intensity conditioning', 'Mobility maintenance', 'Monitor energy throughout'];
    case 'orange':
      return ['Technique work', 'Mobility & flexibility', 'Tempo runs', 'Low-level power (no max effort)'];
    case 'red':
      return ['Soft tissue / foam rolling', 'Breathing & parasympathetic work', 'Light walking', 'Hydration & nutrition focus'];
  }
}

export function evaluateWellness(input: WellnessInput): ReadinessResult {
  const score = calculateReadinessScore(input);
  const status = getReadinessStatus(score);
  const meta = STATUS_META[status];
  return {
    score,
    status,
    statusLabel: meta.label,
    statusColor: meta.color,
    statusBg: meta.bg,
    statusBorder: meta.border,
    trainingAdjustment: getTrainingAdjustment(status, input),
    interpretation: getInterpretation(score, input),
    suggestedFocus: getSuggestedFocus(status, input),
  };
}
